import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import { Input, Select, RadioButton } from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldText,
  getFieldDate,
  clearError,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";
import {
  getCarePlanDetails,
  getClientCarePlansPromise,
  getAllergies,
} from "../services/soar";

function NVTFCCaseTransfer() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [loadStatus, setLoadStatus] = useState({});
  const [allCarePlans, setAllCarePlans] = useState([]);

  const processCarePlans = useCallback(
    (carePlanResults) => {
      console.group("Begin Processing Care Plans...");
      const carePlans = carePlanResults || allCarePlans;
      console.log("Care Plans:", carePlans);
      let carePlanForProgram = carePlans.filter(
        (plan) =>
          getFieldText(plan.ccx_clientprogram) ===
          data.SelectedProgram["@value"]
      )[0];
      console.log("Care Plan For Program:", carePlanForProgram);

      if (!carePlanForProgram) {
        return;
      }

      const loadCarePlanDetails = async () => {
        const carePlanDetails = await getCarePlanDetails(
          carePlanForProgram.ccx_careplanid
        );

        console.log("carePlanDetails", carePlanDetails);

        if (
          carePlanDetails &&
          carePlanDetails.plannarrative &&
          isMountedRef.current
        ) {
          const obj = { ...data };

          const presentingProblem = carePlanDetails.plannarrative.filter(
            (narrative) => narrative.ccx_FieldName === "TIPP"
          )[0];
          const contactPlan = carePlanDetails.plannarrative.filter(
            (narrative) => narrative.ccx_FieldName === "PermanencyPlan"
          )[0];
          const culturalNarrative = carePlanDetails.plannarrative.filter(
            (narrative) => narrative.ccx_FieldName === "CulturalNarrative"
          )[0];
          const safetyPlan = carePlanDetails.plannarrative.filter(
            (narrative) => narrative.ccx_FieldName === "SupervisionNarrative"
          )[0];
          const schoolInfo = carePlanDetails.plannarrative.filter(
            (narrative) => narrative.ccx_FieldName === "School"
          )[0];
          const diagnoses = carePlanDetails.clientdiagnosis
            .filter((dx) => dx.ccx_Diagnosis)
            .map((dx) => dx.ccx_Diagnosis)
            .join("\n");
          const medications = carePlanDetails.clientmedication
            .filter((med) => med.ccx_BrandName)
            .map(
              (med) =>
                `${med.ccx_BrandName}, Dose: ${med.ccx_Dose} ${med.ccx_doseunit}, Notes: ${med.ccx_PatientNotes}, Prescriber: ${med.ccx_Prescriber}`
            )
            .join("\n");

          if (presentingProblem !== undefined) {
            obj.PresentingProblem["@value"] = presentingProblem.ccx_Value;
          }

          if (contactPlan !== undefined) {
            obj.ContactPlan["@value"] = contactPlan.ccx_Value;
          }

          if (culturalNarrative !== undefined) {
            obj.CulturalNarrative[
              "@value"
            ] = `${carePlanDetails.ccx_strengthsnarrative}
  ${culturalNarrative.ccx_Value}`;
          }

          if (safetyPlan !== undefined) {
            obj.SafetyPlan["@value"] = safetyPlan.ccx_Value;
          }

          if (schoolInfo !== undefined) {
            obj.SchoolInfo["@value"] = schoolInfo.ccx_Value;
          }

          obj.Diagnoses["@value"] = diagnoses;
          obj.Medications["@value"] = medications;
          obj.NextCarePlanDate["@value"] = moment(
            carePlanDetails.ccx_NextReviewDate
          ).format("LL");

          setData(obj);
        }
      };

      loadCarePlanDetails();
      console.groupEnd();
    },
    [data, allCarePlans]
  );

  const processCareTeams = useCallback(() => {
    if (data.CareTeam["@value"]) {
      return;
    }

    console.group("Care Teams");
    console.log("Care Team Members: ", careTeams);
    const monthlyMembers = careTeams.filter((member) => {
      const expirationDate =
        member.ccx_expdate !== undefined
          ? moment(getFieldDate(member.ccx_expdate))
          : undefined;

      if (
        getFieldText(member.ccx_clientprogram) !==
        data.SelectedProgram["@value"]
      ) {
        return false;
      } else if (
        expirationDate !== undefined &&
        expirationDate.isBefore(moment())
      ) {
        return false;
      } else {
        return true;
      }
    });

    console.log("Monthly Members: ", monthlyMembers);

    const contacts = monthlyMembers.filter(
      (collateral) => getFieldText(collateral.ccx_membertype) === "803080001"
    );
    contacts.sort((a, b) => {
      if (a.ccx_name > b.ccx_name) return 1;
      else if (a.ccx_name < b.ccx_name) return -1;
      return 0;
    });

    const providers = monthlyMembers.filter(
      (collateral) => getFieldText(collateral.ccx_membertype) === "803080002"
    );
    providers.sort((a, b) => {
      if (a.ccx_name > b.ccx_name) return 1;
      else if (a.ccx_name < b.ccx_name) return -1;
      return 0;
    });

    const sortedMembers = [...contacts, ...providers];
    const membersForProgram = sortedMembers
      .map((member) => {
        return member.ccx_memberid !== undefined
          ? `${member["user.firstlast"]}, ${getFieldName(
              member.sor_providerrole
            )}`
          : `${member["con.firstlast"]}, ${getFieldName(
              member.sor_contactrole
            )}`;
      })
      .join("\n");

    console.log("Processed: ", membersForProgram);

    const obj = { ...data };
    obj.CareTeam["@value"] = membersForProgram;
    setData(obj);

    if (errors.CareTeam !== undefined) {
      clearError(errors, setErrors, "CareTeam");
    }

    console.groupEnd();
  }, [data, careTeams, errors]);

  const handleSelectedProgramChange = (e) => {
    handleChange(e);

    if (!clientData.programs) {
      return;
    }

    const selectedProgram = clientData.programs.filter(
      (option) => option.ccx_client_programid === e.currentTarget.value
    )[0];

    if (!selectedProgram) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = selectedProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(selectedProgram.ccx_programunit);
    setFormsyDetails(details);

    if (!loadStatus.carePlansLoaded) {
      return;
    }

    processCarePlans();
    processCareTeams();
  };

  useEffect(() => {
    if (
      submissionid ||
      !data.SelectedProgram ||
      loadStatus.loadingCarePlans ||
      loadStatus.carePlansLoaded
    ) {
      return;
    }

    const loadCarePlans = async () => {
      setLoadStatus({
        ...loadStatus,
        loadingCarePlans: true,
        carePlansLoaded: false,
      });
      const response = await getClientCarePlansPromise(clientid);

      if (!isMountedRef.current) {
        return;
      }

      const carePlanResults = response.filter(
        (carePlan) =>
          (carePlan.ccx_expirationdate === undefined ||
            moment(getFieldDate(carePlan.ccx_expirationdate)).isAfter(
              moment()
            )) &&
          getFieldName(carePlan.ccx_careplan_status) === "Active"
      );

      if (carePlanResults) {
        setAllCarePlans(carePlanResults);
      }

      setLoadStatus({
        ...loadStatus,
        loadingCarePlans: false,
        carePlansLoaded: true,
      });
      processCarePlans(carePlanResults);
    };

    loadCarePlans();
  }, [data.SelectedProgram, loadStatus, clientid, submissionid]);

  useEffect(() => {
    if (
      submissionid ||
      !data.SelectedProgram ||
      loadStatus.loadingAllergies ||
      loadStatus.allergiesLoaded
    ) {
      return;
    }

    const loadAllergies = async () => {
      setLoadStatus({
        ...loadStatus,
        loadingAllergies: true,
        allergiesLoaded: false,
      });
      const response = await getAllergies(clientid);

      if (!isMountedRef.current) {
        return;
      }

      const allergyResults = response
        .filter((allergy) => !allergy.ccx_expdate)
        .map(
          (allergy) =>
            `${allergy.ccx_name} - ${getFieldName(allergy.ccx_severity)}`
        );

      const obj = { ...data };
      data.MedicalIssues["@value"] = allergyResults.join(", ");
      setLoadStatus({
        ...loadStatus,
        loadingAllergies: false,
        allergiesLoaded: true,
      });
    };

    loadAllergies();
  }, [data.SelectedProgram, loadStatus, clientid, submissionid]);

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoNAOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "N/A", name: "N/A" },
  ];

  const contractTypeOptions = [
    { _id: "DFS HLOC", name: "DFS HLOC" },
    { _id: "DFS SIB", name: "DFS SIB" },
    { _id: "DCFS", name: "DCFS" },
    { _id: "State Parole", name: "State Parole" },
    { _id: "Clark Count Probation", name: "Clark Count Probation" },
    { _id: "Washoe County", name: "Washoe County" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          {formsyDetails.ClientProgramName}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <Input
                name="CareTeam"
                label="Care Team Members:"
                error={errors.CareTeam}
                value={data.CareTeam["@value"]}
                onChange={handleChange}
                identical={compare("CareTeam")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="h5">
                <u>Clinical Info</u>
              </div>
              <Input
                name="Diagnoses"
                label="Child's Diagnoses:"
                error={errors.Diagnoses}
                value={data.Diagnoses["@value"]}
                onChange={handleChange}
                identical={compare("Diagnoses")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <ul>
                <li>
                  <Input
                    name="PresentingProblem"
                    label="Presenting problems:"
                    error={errors.PresentingProblem}
                    value={data.PresentingProblem["@value"]}
                    onChange={handleChange}
                    identical={compare("PresentingProblem")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <li>
                  <Input
                    name="ContactPlan"
                    label="Plan for Primary Family Contact:"
                    error={errors.ContactPlan}
                    value={data.ContactPlan["@value"]}
                    onChange={handleChange}
                    identical={compare("ContactPlan")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <li>
                  <Input
                    name="CulturalNarrative"
                    label="Strengths and Cultural Narrative:"
                    error={errors.CulturalNarrative}
                    value={data.CulturalNarrative["@value"]}
                    onChange={handleChange}
                    identical={compare("CulturalNarrative")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <li>
                  <Input
                    name="SafetyPlan"
                    label="Supervision and Safety Planning Narrative:"
                    error={errors.SafetyPlan}
                    value={data.SafetyPlan["@value"]}
                    onChange={handleChange}
                    identical={compare("SafetyPlan")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </li>
                <ul>
                  <li>
                    <RadioButton
                      name="PlanReviewed"
                      label="Safety Plan has been reviewed with new case manager:"
                      error={errors.PlanReviewed}
                      value={data.PlanReviewed["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      identical={compare("PlanReviewed")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </li>
                </ul>
              </ul>
              <div className="h5">
                <u>Case Management Info</u>
              </div>
              <RadioButton
                name="ContractType"
                label="What contract are they under?"
                error={errors.ContractType}
                value={data.ContractType["@value"]}
                options={contractTypeOptions}
                onChange={handleChange}
                identical={compare("ContractType")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="NextCarePlanDate"
                label="Care Plan Expiration Date:"
                error={errors.NextCarePlanDate}
                value={data.NextCarePlanDate["@value"]}
                onChange={handleChange}
                identical={compare("NextCarePlanDate")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="BioVisits"
                label="Bio family visits scheduled?"
                error={errors.BioVisits}
                value={data.BioVisits["@value"]}
                options={yesNoOptions}
                onChange={handleChange}
                identical={compare("BioVisits")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.BioVisits["@value"] !== "No" ? (
                <Input
                  name="BioVisitsDetails"
                  label="When:"
                  error={errors.BioVisitsDetails}
                  value={data.BioVisitsDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("BioVisitsDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <Input
                name="MedicalIssues"
                label="Medical Issues/Needs:"
                error={errors.MedicalIssues}
                value={data.MedicalIssues["@value"]}
                onChange={handleChange}
                identical={compare("MedicalIssues")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Medications"
                label="Medications:"
                error={errors.Medications}
                value={data.Medications["@value"]}
                onChange={handleChange}
                identical={compare("Medications")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="Psychiatrist"
                label="Psychiatrist?"
                error={errors.Psychiatrist}
                value={data.Psychiatrist["@value"]}
                options={yesNoOptions}
                onChange={handleChange}
                identical={compare("Psychiatrist")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Psychiatrist["@value"] !== "No" ? (
                <Input
                  name="PsychiatristName"
                  label="Name:"
                  error={errors.PsychiatristName}
                  value={data.PsychiatristName["@value"]}
                  onChange={handleChange}
                  identical={compare("PsychiatristName")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <Input
                name="UpcomingAppts"
                label="Upcoming Appointments (med appts, TTM's, medical/dental, etc.)?"
                error={errors.UpcomingAppts}
                value={data.UpcomingAppts["@value"]}
                onChange={handleChange}
                identical={compare("UpcomingAppts")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="SchoolInfo"
                label="School Info:"
                error={errors.SchoolInfo}
                value={data.SchoolInfo["@value"]}
                onChange={handleChange}
                identical={compare("SchoolInfo")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <hr />
              <div className="h5">
                Incoming Case Manager's Clinical Information and Notes
              </div>
              <Input
                name="Triggers"
                label="Can you identify any triggers for the child?"
                error={errors.Triggers}
                value={data.Triggers["@value"]}
                onChange={handleChange}
                identical={compare("Triggers")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="DeescalateTechniques"
                label="What techniques work to de-escalate the child or keep them calm to begin with?"
                error={errors.DeescalateTechniques}
                value={data.DeescalateTechniques["@value"]}
                onChange={handleChange}
                identical={compare("DeescalateTechniques")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="UnnoticedBehaviors"
                label="If the child is relatively stable, are there any behaviors or symptoms that could go unnoticed to a person who doesn't know the child?"
                error={errors.UnnoticedBehaviors}
                value={data.UnnoticedBehaviors["@value"]}
                onChange={handleChange}
                identical={compare("UnnoticedBehaviors")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="FPPositives"
                label="What are the positive qualities of your foster parent (Very therapeutic, transports to everything, advocates for their youth, great at paperwork, etc.)?"
                error={errors.FPPositives}
                value={data.FPPositives["@value"]}
                onChange={handleChange}
                identical={compare("FPPositives")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="FPTechniques"
                label="What techniques seem to work really well with your parent (check-in phone calls a few times per week, mostly email or text communication, patience and attentiveness, maintaining your calm to bring their stress level down, etc.)?"
                error={errors.FPTechniques}
                value={data.FPTechniques["@value"]}
                onChange={handleChange}
                identical={compare("FPTechniques")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <span className="d-print-none">
              <Signature
                canvasWidth="450"
                canvasHeight="200"
                modalTitle="Report Signatures"
                careTeams={careTeams}
                signatures={signatures}
                onSign={saveSignature}
                clientData={clientData.clientFirst}
                showModal={showSignatureModal}
                onHide={() => {
                  setShowSignatureModal(false);
                }}
                finalSignType={
                  formsyConfig.HelperConfiguration &&
                  JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
                }
                initialSignatureType={
                  formsyConfig.HelperConfiguration &&
                  JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
                }
              ></Signature>
            </span>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default NVTFCCaseTransfer;
